import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { faSearchPlus, faGear } from "@fortawesome/free-solid-svg-icons";

import CustomForm from "../general/form/CustomForm";
import OptionSelector from "../general/auxiliar/ui/OptionSelector";
import useFormWithDisable from "../../auxiliar/customHooks/useFormWithDisable";
import {
    previewInvoiceTemplate,
    putInvoiceTemplate,
    getInvoiceTemplate,
} from "../../actions/invoice";

const InvoiceTemplate = () => {
    const {
        register,
        handleSubmit,
        handleSubmitWithoutValidation,
        fetchFormValues,
        resetFetchedValues,
        watch,
        errors,
        isSubmitting,
        isLoadingData,
    } = useFormWithDisable(true);

    const clinicId = useSelector((state) => state.activeClinic.id);
    const vertical = watch("vertical_text");
    const gdpr = watch("gdpr_text");
    const footer = watch("footer");

    const [isEditing, setIsEditing] = useState(false);
    const [selected, setSelected] = useState("basic");

    const formConfig = {
        vertical_text: {
            condition: true,
            disabled: !isEditing,
            name: "vertical_text",
            label: `Texto vertical (${vertical?.length || 0}/150)`,
            type: "textarea",
            validators: {
                maxLength: {
                    value: 150,
                    message: "El máximo de caracteres aceptado es 150",
                },
            },
        },
        gdpr_text: {
            condition: true,
            disabled: !isEditing,
            name: "gdpr_text",
            label: `Texto principal (${gdpr?.length || 0}/1000)`,
            type: "textarea",
            rows: 6,
            validators: {
                maxLength: {
                    value: 1000,
                    message: "El máximo de caracteres aceptado es 1000",
                },
            },
        },
        footer: {
            condition: true,
            disabled: !isEditing,
            name: "footer",
            label: `Pie de página (${footer?.length || 0}/200)`,
            type: "textarea",
            validators: {
                maxLength: {
                    value: 200,
                    message: "El máximo de caracteres aceptado es 200",
                },
            },
        },
    };

    const options = {
        basic: {
            condition: true,
            title: "Configuración",
            icon: faGear,
            selected: selected === "basic",
            onClick: handleSubmitWithoutValidation(setSelected, "basic"),
        },
        advanced: {
            condition: true,
            title: "Personalización",
            icon: faSearchPlus,
            selected: selected === "advanced",
            onClick: handleSubmitWithoutValidation(setSelected, "advanced"),
        },
    };

    useEffect(() => {
        fetchFormValues(getInvoiceTemplate, clinicId);
    }, [clinicId]);

    useEffect(() => {
        if (!isEditing && clinicId) resetFetchedValues();
    }, [isEditing]);

    return (
        <div>
            <h2 className="sr-only">
                Plantilla de Facturas de fisioterapia – Personaliza tu Factura con MiFisio Agenda
            </h2>

            <section className="mb-8">
                <h3 className="text-primary text-2xl">Plantilla de Facturas</h3>

                <p className="lg:text-lg mt-3">
                    Personaliza los textos de las facturas generadas por MiFisio Agenda utilizando
                    el formulario a continuación.
                </p>

                <form className="my-3">
                    <CustomForm
                        formConfig={formConfig}
                        errors={errors}
                        register={register}
                        className="grid grid-cols-1 gap-3"
                        isLoading={isLoadingData}
                    />

                    <div className="mt-5 flex space-x-2 sm:space-x-4 justify-between">
                        <div className="flex space-x-2 sm:space-x-4">
                            {isEditing && (
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={handleSubmit(
                                        putInvoiceTemplate,
                                        clinicId,
                                        handleSubmitWithoutValidation(setIsEditing, false),
                                    )}
                                    disabled={isSubmitting}
                                >
                                    Guardar
                                </button>
                            )}

                            <button
                                className="btn btn-primary btn-sm"
                                onClick={handleSubmitWithoutValidation(setIsEditing, !isEditing)}
                            >
                                {isEditing ? "Cancelar" : "Editar"}
                            </button>
                        </div>

                        <button
                            className="btn btn-primary btn-sm"
                            onClick={handleSubmit(previewInvoiceTemplate, clinicId)}
                            disabled={isSubmitting}
                        >
                            Previsualizar
                        </button>
                    </div>
                </form>
            </section>

            <section className="mx-auto w-full bg-base-100 grid grid-cols-1 md:grid-cols-3 gap-16 lg:gap-20 px-8">
                <div className="md:col-span-2">
                    <div className="flex justify-center">
                        <div className="bg-base-200 shadow-xl w-full rounded-lg p-8">
                            <div className="flex items-center justify-between">
                                <h3 className="text-secondary text-2xl">
                                    {options[selected]?.title}
                                </h3>

                                {/* Displayed service selector */}
                                <OptionSelector options={options} />
                            </div>

                            {/* Basic tutorial */}
                            {selected === "basic" && (
                                <>
                                    <p className="text-base lg:text-lg my-3">
                                        MiFisio Agenda te permite generar y enviar facturas de
                                        manera eficiente a tus pacientes. Se recomienda configurar
                                        la plantilla de facturación para reflejar la imagen
                                        profesional de tu clínica.
                                    </p>

                                    <p className="text-base lg:text-lg mt-3">
                                        La imagen muestra un ejemplo de factura generada por MiFisio
                                        Agenda, destacando los elementos personalizables que puedes
                                        ajustar. Estos elementos incluyen:
                                    </p>

                                    <ul className="text-base lg:text-lg mb-3 px-3 list-disc list-inside">
                                        <li>
                                            Logo: El logo de la clínica se mostrará automáticamente
                                            en la parte superior izquierda.
                                        </li>
                                        <li>
                                            Texto Vertical: Inserta un breve mensaje en el margen
                                            izquierdo de la factura.
                                        </li>
                                        <li>
                                            Texto Principal: Agrega hasta 1000 caracteres de
                                            información en la parte inferior de la factura.
                                        </li>
                                        <li>
                                            Pie de Página: Añade un pequeño mensaje o información
                                            adicional en el pie de la factura.
                                        </li>
                                    </ul>

                                    <p className="text-base lg:text-lg my-3">
                                        El equipo de MiFisio Agenda está siempre disponible para
                                        responder tus dudas o recibir tus sugerencias. No dudes en{" "}
                                        <a href="mailto:info@mifisioagenda.com" className="link">
                                            contactarnos
                                        </a>
                                        .
                                    </p>
                                </>
                            )}

                            {selected === "advanced" && (
                                <>
                                    <p className="text-base lg:text-lg my-3">
                                        Para personalizar aún más tu factura, puedes utilizar
                                        etiquetas HTML que permiten modificar el formato del texto,
                                        aplicando negrita, cursiva, subrayado o añadiendo saltos de
                                        línea para mejorar la presentación.
                                    </p>

                                    <p className="text-base lg:text-lg mt-3">
                                        El intérprete de etiquetas de MiFisio Agenda soporta HTML,
                                        lo que te permite utilizar cualquier etiqueta válida para
                                        personalizar el contenido. Si no estás familiarizado con
                                        HTML, aquí tienes algunas etiquetas recomendadas:
                                    </p>

                                    <ul className="text-base lg:text-lg mb-3 px-3 list-disc list-inside">
                                        <li>
                                            Etiqueta &lt;strong&gt;: Resalta el texto en negrita.
                                            Por ejemplo, el código &ldquo;&lt;strong&gt;Texto en
                                            negrita&lt;/strong&gt;&rdquo; mostrará:{" "}
                                            <strong>Texto en negrita</strong>.
                                        </li>
                                        <li>
                                            Etiqueta &lt;u&gt;: Resalta el texto subrayado. Por
                                            ejemplo, el código &ldquo;&lt;u&gt;Texto
                                            subrayado&lt;/u&gt;&rdquo; mostrará:{" "}
                                            <u>Texto subrayado</u>.
                                        </li>
                                        <li>
                                            Etiqueta &lt;i&gt;: Muestra el texto en cursiva. Por
                                            ejemplo, el código &ldquo;&lt;i&gt;Texto en
                                            cursiva&lt;/i&gt;&rdquo; mostrará:{" "}
                                            <i>Texto en cursiva</i>.
                                        </li>
                                    </ul>

                                    <p className="text-base lg:text-lg my-3">
                                        Puedes combinar estas etiquetas para resaltar aún más tu
                                        contenido. Por ejemplo, el código
                                        &ldquo;&lt;strong&gt;&lt;u&gt;Texto en negrita y
                                        subrayado&lt;/u&gt;&lt;/strong&gt;&rdquo; mostrará:{" "}
                                        <strong>
                                            <u>Texto en negrita y subrayado</u>
                                        </strong>
                                        .
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Example invoice */}
                <div className="flex flex-col gap-4 lg:gap-8 items-center">
                    <div>
                        <img
                            src={require("../../media/Tutorial/invoice_example.png")}
                            alt={"Ejemplo de factura personalizada de MiFisio Agenda"}
                        />
                    </div>
                </div>
            </section>
        </div>
    );
};

export default InvoiceTemplate;
